import { DEFAULT_STATUS_FOR_ARCHIVED_SEARCH, DEFAULT_STATUS_FOR_ONLINE_SEARCH } from '@/utils/queryParams';
import {
    DELETE_SAVE_SEARCH_FAIL,
    DELETE_SAVE_SEARCH_REQUEST,
    DELETE_SAVE_SEARCH_SUCCESS,
    LOG_OUT_BIDDER,
    SAVE_SEARCH_FAIL,
    SAVE_SEARCH_REQUEST,
    SAVE_SEARCH_SUCCESS,
} from './actions';
import { getFollowSearchAnalytics, getUnfollowSearchAnalytics } from './analytics';
import { getSavedSearchIdByKey, submitIsSavedSearchCheck } from './saveSearchCheck';
import api from '@/redux/api/saveSearch';
import cloneDeep from 'lodash/cloneDeep';
import { fetchSavedSearchesIfNeeded } from './savedSearch';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';

// reducer
const DEFAULT_STATE = {
    byParams: {},
    error: false,
    //TODO remove these and update places using them to use byParams
    submitted: false,
    success: false,
};

export type State = {
    byParams: {
        [key: string]: {
            deleted: boolean;
            key: string;
            submitted: boolean;
            success: boolean;
        };
    };
    deleted?: boolean;
    error: boolean;
    submitted: boolean;
    success: boolean;
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    const existing = cloneDeep(state.byParams);
    switch (action.type) {
        case SAVE_SEARCH_REQUEST:
        case DELETE_SAVE_SEARCH_REQUEST:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload}`]: {
                        deleted: false,
                        key: `${action.payload}`,
                        submitted: true,
                        success: false,
                    },
                },
                submitted: true,
                success: false,
            };
        case SAVE_SEARCH_SUCCESS:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload}`]: {
                        deleted: false,
                        key: `${action.payload}`,
                        submitted: false,
                        success: true,
                    },
                },
                submitted: false,
                success: true,
            };
        case LOG_OUT_BIDDER:
            return { ...DEFAULT_STATE };
        case DELETE_SAVE_SEARCH_SUCCESS:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload}`]: {
                        deleted: true,
                        key: `${action.payload}`,
                        submitted: false,
                        success: true,
                    },
                },
                submitted: false,
                success: true,
            };
        case SAVE_SEARCH_FAIL:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload}`]: {
                        deleted: false,
                        key: `${action.payload}`,
                        submitted: false,
                        success: false,
                    },
                },
                error: true,
                submitted: false,
                success: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
export const getSaveSearchState = (state: GlobalState) => state.saveSearch;

export const getSaveSearchParams = (queryParams: any, pathname: string = '') => {
    const newParams = cloneDeep(queryParams);

    if (newParams.status === DEFAULT_STATUS_FOR_ARCHIVED_SEARCH) {
        newParams.status = DEFAULT_STATUS_FOR_ONLINE_SEARCH;
    }

    delete newParams.pageSize;
    delete newParams.pagesize;
    delete newParams.page;
    delete newParams.sort;
    delete newParams.saveSearch;

    newParams.keyword = newParams.keyword ? newParams.keyword : '';

    if (pathname.includes('/c/') && !newParams.campaign_id) {
        let pathCategoryIds: any = pathname.match(/\d+/g);
        pathCategoryIds = pathCategoryIds ? pathCategoryIds.join() : '';

        if (!newParams.category_id) {
            newParams.category_id = pathCategoryIds;
        } else {
            newParams.category_id = `${pathCategoryIds},${newParams.category_id}`;
        }
    }

    return newParams;
};

export const createSaveSearchParams = (categoryId: string) => {
    return {
        category_id: categoryId,
    };
};

export const createSaveSearchKey = (categoryId: string) => {
    return createSaveSearchKeyBySearchParams(createSaveSearchParams(categoryId));
};

export const createSaveSearchKeyBySearchParams = (searchParams: any): string => {
    if (!searchParams) {
        return '';
    }
    const sortedParams = {};
    Object.keys(searchParams)
        .sort()
        .forEach((key) => {
            // replace all wierd whitespace with standard spaces, and trim the ends
            sortedParams[key] =
                (typeof searchParams?.[key] === 'string' ? searchParams[key] : '').replace(/\s+/g, ' ').trim() ||
                sortedParams[key];
        });
    let key = JSON.stringify(sortedParams);
    key = key.replace(/}|{|"/g, '');
    return key;
};

/* ACTION CREATORS */
export const submitSaveSearch = (ssParams: any, sendAlert: boolean) => async (dispatch: any, getState: any) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const analytics = getFollowSearchAnalytics();
        const savedSearchPayload = createSaveSearchKeyBySearchParams(ssParams);

        dispatch({
            payload: savedSearchPayload,
            type: SAVE_SEARCH_REQUEST,
        });
        await api.postSaveSearch({ authToken, deployment, sendAlert, ssParams });
        dispatch({
            meta: analytics,
            payload: savedSearchPayload,
            type: SAVE_SEARCH_SUCCESS,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
        dispatch(submitIsSavedSearchCheck(ssParams));
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: SAVE_SEARCH_FAIL,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
        dispatch(submitIsSavedSearchCheck(ssParams));
    }
};

export const deleteSavedSearch = (ssParams: any) => async (dispatch: any, getState: any) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const saveSearchKey = createSaveSearchKeyBySearchParams(ssParams);
        const savedSearchId = getSavedSearchIdByKey(state, saveSearchKey);
        const analytics = getUnfollowSearchAnalytics();

        dispatch({
            payload: createSaveSearchKeyBySearchParams(ssParams),
            type: DELETE_SAVE_SEARCH_REQUEST,
        });

        await api.postDeleteSavedSearch({ authToken, deployment, savedSearchId });
        dispatch({
            meta: analytics,
            payload: createSaveSearchKeyBySearchParams(ssParams),
            type: DELETE_SAVE_SEARCH_SUCCESS,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
        dispatch(submitIsSavedSearchCheck(ssParams));
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: DELETE_SAVE_SEARCH_FAIL,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
        dispatch(submitIsSavedSearchCheck(ssParams));
    }
};
